import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Send, Lock, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, money, dateShort } from "@/lib/api";
import { SectionTitle, Pill, Field, EmptyState } from "@/components/Bits";

const input = "w-full rounded-sm border border-line bg-white px-3 py-2 text-sm outline-none focus:border-lime";

export default function OptionSetsPanel({ projectId, scenarios = [], spaces = [] }) {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", space_id: "", note: "", a: "", aLabel: "Option A", b: "", bLabel: "Option B" });

  const load = () =>
    api.get("/option-sets", { params: { project_id: projectId } }).then((r) => setRows(r.data)).catch(() => setRows([]));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const create = async (e) => {
    e.preventDefault();
    if (form.a === form.b) return toast.error("Pick two different scenarios");
    try {
      await api.post("/option-sets", {
        name: form.name,
        project_id: projectId,
        space_id: form.space_id || null,
        note: form.note || null,
        options: [
          { scenario_id: form.a, label: form.aLabel },
          { scenario_id: form.b, label: form.bLabel },
        ],
      });
      toast.success("Options created — publish when you're ready");
      setCreating(false);
      setForm({ name: "", space_id: "", note: "", a: "", aLabel: "Option A", b: "", bLabel: "Option B" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-8" data-testid="option-sets-panel">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <SectionTitle>Shared Design Options</SectionTitle>
        <button
          data-testid="create-option-set-btn"
          onClick={() => setCreating((v) => !v)}
          disabled={scenarios.length < 2}
          className="inline-flex items-center gap-2 rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark disabled:opacity-50"
        >
          <Plus className="h-4 w-4" strokeWidth={1.5} /> Share Options With Client
        </button>
      </div>
      <p className="max-w-2xl text-xs leading-relaxed text-fog">
        Publish two scenarios side by side as friendly options. The client sees your labels and their
        own prices only — never scenario names, costs or margins. Their choice never changes the live
        project; you still apply the scenario yourself.
      </p>

      {creating && (
        <form onSubmit={create} className="card-surface space-y-5 p-6" data-testid="option-set-form">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Title the client sees">
              <input data-testid="option-set-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Living Room — Choose Your Direction" className={input} />
            </Field>
            <Field label="Room (optional)">
              <select data-testid="option-set-space" value={form.space_id} onChange={(e) => setForm({ ...form, space_id: e.target.value })} className={input}>
                <option value="">Whole project</option>
                {spaces.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
            <Field label="First scenario">
              <select data-testid="option-set-a" required value={form.a} onChange={(e) => setForm({ ...form, a: e.target.value })} className={input}>
                <option value="">Select…</option>
                {scenarios.map((s) => <option key={s.id} value={s.id}>{s.name} · {money(s.total)}</option>)}
              </select>
            </Field>
            <Field label="Label for the client">
              <input data-testid="option-set-a-label" required value={form.aLabel} onChange={(e) => setForm({ ...form, aLabel: e.target.value })} className={input} />
            </Field>
            <Field label="Second scenario">
              <select data-testid="option-set-b" required value={form.b} onChange={(e) => setForm({ ...form, b: e.target.value })} className={input}>
                <option value="">Select…</option>
                {scenarios.map((s) => <option key={s.id} value={s.id}>{s.name} · {money(s.total)}</option>)}
              </select>
            </Field>
            <Field label="Label for the client">
              <input data-testid="option-set-b-label" required value={form.bLabel} onChange={(e) => setForm({ ...form, bLabel: e.target.value })} className={input} />
            </Field>
            <Field label="Note to the client" className="sm:col-span-2">
              <textarea data-testid="option-set-note" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={input} />
            </Field>
          </div>
          <div className="flex gap-3">
            <button data-testid="option-set-submit" type="submit" className="rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">Create</button>
            <button type="button" onClick={() => setCreating(false)} className="rounded-sm border border-line bg-white px-4 py-2.5 text-sm text-ink hover:bg-line/50">Cancel</button>
          </div>
        </form>
      )}

      {rows.length === 0 ? (
        <EmptyState testId="option-sets-empty" title="No options shared yet" body="Build two scenarios, then publish them as a client-friendly choice." />
      ) : (
        <div className="space-y-5" data-testid="option-sets-list">
          {rows.map((r) => (
            <div key={r.id} className="card-surface p-6" data-testid={`option-set-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <Link to={`/options/${r.id}`} className="font-serif text-xl text-ink hover:text-lime-dark">{r.name}</Link>
                  <div className="mt-1 text-xs text-fog">
                    {r.space_name || "Whole project"} · {r.options.length} options
                    {r.published_at ? ` · shared ${dateShort(r.published_at)}` : ""}
                  </div>
                </div>
                <Pill tone={r.status === "Published" ? "bg-lime text-ink" : r.status === "Locked" ? "bg-ink text-white" : "bg-line text-stone"}>{r.status}</Pill>
              </div>

              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                {r.options.map((o) => (
                  <div key={o.scenario_id} className={`rounded-sm border p-4 ${r.chosen_scenario_id === o.scenario_id ? "border-lime bg-[#F4F5E4]" : "border-line"}`}>
                    <div className="text-sm text-ink">{o.label}</div>
                    <div className="num mt-1 text-lg text-ink">{money(o.total)}</div>
                    {r.chosen_scenario_id === o.scenario_id && (
                      <div className="mt-2 text-[11px] uppercase tracking-[0.15em] text-lime-dark" data-testid={`option-set-chosen-${r.id}`}>
                        Client’s choice
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {r.chosen_scenario_id && (
                <div className="mt-4 text-xs text-stone">
                  {r.chosen_by_name} chose on {dateShort(r.chosen_at)} — apply that scenario when you’re ready.
                </div>
              )}

              <div className="mt-5 flex flex-wrap gap-3">
                {r.status === "Draft" && (
                  <button
                    data-testid={`publish-option-set-${r.id}`}
                    onClick={() => act(() => api.post(`/option-sets/${r.id}/publish`), "Shared with the client")}
                    className="inline-flex items-center gap-2 rounded-sm bg-lime px-4 py-2 text-xs font-medium text-ink hover:bg-lime-dark"
                  >
                    <Send className="h-3.5 w-3.5" strokeWidth={1.5} /> Publish to client
                  </button>
                )}
                {r.status === "Published" && (
                  <button
                    data-testid={`lock-option-set-${r.id}`}
                    onClick={() => act(() => api.patch(`/option-sets/${r.id}`, { status: "Locked" }), "Decision locked")}
                    className="inline-flex items-center gap-2 rounded-sm border border-line px-4 py-2 text-xs text-stone hover:border-fog"
                  >
                    <Lock className="h-3.5 w-3.5" strokeWidth={1.5} /> Lock the choice
                  </button>
                )}
                {r.status === "Locked" && (
                  <button
                    data-testid={`unlock-option-set-${r.id}`}
                    onClick={() => act(() => api.patch(`/option-sets/${r.id}`, { status: "Published" }), "Reopened for the client")}
                    className="rounded-sm border border-line px-4 py-2 text-xs text-stone hover:border-fog"
                  >
                    Reopen for the client
                  </button>
                )}
                <Link to={`/options/${r.id}`} className="rounded-sm border border-line px-4 py-2 text-xs text-stone hover:border-fog">
                  Preview client view
                </Link>
                <button
                  data-testid={`delete-option-set-${r.id}`}
                  onClick={() => act(() => api.delete(`/option-sets/${r.id}`), "Options removed")}
                  className="rounded-sm border border-line p-2 text-stone hover:border-clay hover:text-clay"
                  aria-label="Delete options"
                >
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
